import { onScopeDispose, ref, shallowRef, watch } from 'vue';
import type { Ref, ShallowRef } from 'vue';
import { StoreSetupService } from '@/api/services/store-setup.service';
import { useUi } from '@/composition/use-ui';
import { getServerErrorCode } from '@/domain/reservations/reservation-errors';
import { setupErrorKey } from '@/domain/store-setup/setup-errors';
import { AlertType } from '@/interfaces/IAlert';
import type {
  ISetupPatchDto,
  IStoreSetupStatus,
} from '@/interfaces/IStoreSetup';
import { i18n } from '@/plugins/i18n';

export interface IUseStoreSetupStatus {
  /** null until loaded (and after the store changes); `status.setup` is null for stores created outside the flow. */
  status: ShallowRef<IStoreSetupStatus | null>;
  loading: Ref<boolean>;
  /** `GET /store-setup/:id`; resolves null (never throws) when there is no store, it failed or the store changed meanwhile. */
  load(): Promise<IStoreSetupStatus | null>;
  /** `PATCH /store-setup/:id`, then a reload; false (with a toast) when it failed. */
  patch(body: ISetupPatchDto): Promise<boolean>;
}

/**
 * Setup state of a store (plan §4.9, §4.10, §10.4): the setup page resolves
 * its screen from it and the dashboard builds the checklist and the «Su menú»
 * card. Answers for another store than the current one are dropped.
 */
export function useStoreSetupStatus(
  restaurantId: () => number | null
): IUseStoreSetupStatus {
  const ui = useUi();
  const status = shallowRef<IStoreSetupStatus | null>(null);
  const loading = ref(false);
  let generation = 0;
  let disposed = false;

  async function load(): Promise<IStoreSetupStatus | null> {
    const id = restaurantId();
    const current = ++generation;
    if (!id) {
      status.value = null;
      loading.value = false;
      return null;
    }
    loading.value = true;
    try {
      const loaded = await StoreSetupService.status(id);
      if (disposed || current !== generation || restaurantId() !== id)
        return null;
      status.value = loaded;
      return loaded;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      if (!disposed && current === generation) loading.value = false;
    }
  }

  async function patch(body: ISetupPatchDto): Promise<boolean> {
    const id = restaurantId();
    if (!id) return false;
    try {
      await StoreSetupService.patch(id, body);
    } catch (error) {
      console.error(error);
      if (!disposed) {
        const key =
          setupErrorKey(getServerErrorCode(error)) ??
          'storeSetup.errors.generic';
        ui.showToast(AlertType.Error, String(i18n.t(key)));
      }
      return false;
    }
    if (!disposed && restaurantId() === id) await load();
    return true;
  }

  // The status of another store must never show, not even for a moment. Sync,
  // so it always runs before a consumer's own watcher reloads the new store.
  watch(
    restaurantId,
    () => {
      generation++;
      status.value = null;
      loading.value = false;
    },
    { flush: 'sync' }
  );

  onScopeDispose(() => {
    disposed = true;
  });

  return { status, loading, load, patch };
}
